import { createContext, useContext, useEffect, useRef } from 'react';
import type { ClientFrame, RealtimeEnvelope, RealtimeStatus } from './protocol';

// Kept apart from the provider component so React Fast Refresh can preserve
// state while the provider is edited (the same split as lib/authContext.ts).

export interface RealtimeContextValue {
  status: RealtimeStatus;
  subscribe: (fn: (env: RealtimeEnvelope) => void) => () => void;
  send: (frame: ClientFrame) => void;
}

export const RealtimeContext = createContext<RealtimeContextValue>({
  status: 'idle',
  subscribe: () => () => undefined,
  send: () => undefined,
});

/** Send a frame to the server from this tab (RealtimeClient.send). */
export function useRealtimeSend(): (frame: ClientFrame) => void {
  return useContext(RealtimeContext).send;
}

/** Where the live connection stands. Screens use it to decide how often to poll. */
export function useRealtimeStatus(): RealtimeStatus {
  return useContext(RealtimeContext).status;
}

/**
 * Run `handler` for every live event of the given type(s).
 *
 * The handler may change between renders without resubscribing: the latest is
 * kept in a ref, so callers can pass an inline function.
 */
export function useRealtimeEvent(
  types: string | readonly string[],
  handler: (env: RealtimeEnvelope) => void,
): void {
  const { subscribe } = useContext(RealtimeContext);
  const latest = useRef(handler);
  latest.current = handler;

  const key = typeof types === 'string' ? types : types.join('|');

  useEffect(() => {
    const wanted = new Set(key.split('|'));
    return subscribe((env) => {
      if (wanted.has(env.type)) latest.current(env);
    });
  }, [subscribe, key]);
}
